import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Skull, Key, Heart, Crosshair, Zap, FlaskConical, Eye, Scan, Grid3X3, Lock, Unlock, ChevronRight, HelpCircle, AlertTriangle, Timer, Award } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import UmbrellaLogo from '@/components/UmbrellaLogo';
import CONFIG from '@/config/siteConfig';
import { PUZZLE_3_MEMORY_MATCH } from '@/config/missionContent';

const CARD_ICONS = [
  { name: 'skull', Icon: Skull, color: '#ef4444' },
  { name: 'key', Icon: Key, color: '#eab308' },
  { name: 'heart', Icon: Heart, color: '#ec4899' },
  { name: 'target', Icon: Crosshair, color: '#f97316' },
  { name: 'zap', Icon: Zap, color: '#3b82f6' },
  { name: 'flask', Icon: FlaskConical, color: '#a855f7' },
  { name: 'eye', Icon: Eye, color: '#14b8a6' },
  { name: 'shield', Icon: Shield, color: '#22c55e' },
];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const getRating = (moves) => {
  if (moves <= 10) return { letter: 'S', color: '#eab308', label: 'PERFECT' };
  if (moves <= 14) return { letter: 'A', color: '#22c55e', label: 'EXCELLENT' };
  if (moves <= 18) return { letter: 'B', color: '#3b82f6', label: 'GOOD' };
  return { letter: 'C', color: '#9ca3af', label: 'CLEARED' };
};

/* ─── Security Background ─── */
function SecurityBackground() {
  const particles = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: `${5 + (i * 9.7) % 90}%`,
      top: `${3 + (i * 11.3) % 94}%`,
      size: 1 + (i % 3) * 0.5,
      duration: 8 + (i % 4) * 3,
      delay: i * 0.8,
      opacity: 0.12 + (i % 3) * 0.06,
      color: i % 2 === 0 ? 'rgba(168,85,247,' : 'rgba(20,184,166,',
    })), []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Dark purple gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#05020a] to-black" />

      {/* Diagonal scan lines */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #a855f7 10px, #a855f7 11px)' }}
      />

      {/* Floating particles */}
      {particles.map(p => (
        <motion.div key={p.id}
          className="absolute rounded-full"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size, background: `${p.color}${p.opacity})` }}
          animate={{ y: [0, -12, 0], x: [0, 4, -2, 0], opacity: [p.opacity, p.opacity * 1.5, p.opacity] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
        />
      ))}

      {/* Moving scan bar */}
      <motion.div
        animate={{ y: ['0%', '200%'] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
        className="absolute inset-x-0 h-32 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(168,85,247,0.03) 50%, transparent 100%)' }}
      />

      {/* Purple vignette */}
      <div className="absolute inset-0 animate-pulse" style={{ animationDuration: '6s', background: 'radial-gradient(ellipse at center, transparent 40%, rgba(40,10,60,0.2) 100%)' }} />

      {/* CRT scanlines */}
      <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(168,85,247,0.1) 2px, rgba(168,85,247,0.1) 4px)', backgroundSize: '100% 4px' }} />
    </div>
  );
}

/* ─── Tutorial Screen ─── */
function MemoryTutorial({ onStart }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-lg mx-auto space-y-4">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <HelpCircle size={14} className="text-purple-400" />
          <span className="text-xs font-terminal tracking-[5px] text-purple-400 uppercase">Security Protocol</span>
          <HelpCircle size={14} className="text-purple-400" />
        </div>
        <p className="text-xs font-terminal text-purple-600 tracking-wider">Review clearance test procedures</p>
      </div>

      {/* How it works */}
      <div className="bg-black/40 border border-purple-900/30 rounded p-4 space-y-3"
        style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)' }}>
        <div className="flex items-center gap-2 pb-2 border-b border-purple-900/20">
          <Grid3X3 size={12} className="text-purple-500" />
          <span className="text-xs font-terminal tracking-[3px] text-purple-500 uppercase">How It Works</span>
        </div>
        <div className="space-y-2.5">
          {[
            { icon: Eye, label: 'Click any card to reveal the hidden symbol', color: 'text-purple-400' },
            { icon: Grid3X3, label: 'Flip two cards per turn — find matching pairs', color: 'text-yellow-400' },
            { icon: Unlock, label: 'Matched pairs stay revealed — clear all 8 pairs', color: 'text-green-400' },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.15 }}
              className="flex items-center gap-3">
              <div className="w-7 h-7 rounded border border-purple-800/30 bg-purple-950/30 flex items-center justify-center flex-shrink-0">
                <item.icon size={14} className={item.color} />
              </div>
              <span className="text-xs font-terminal text-gray-400 tracking-wider">{item.label}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Card states */}
      <div className="bg-black/40 border border-purple-900/30 rounded p-4 space-y-3"
        style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)' }}>
        <div className="flex items-center gap-2 pb-2 border-b border-purple-900/20">
          <AlertTriangle size={12} className="text-yellow-600" />
          <span className="text-xs font-terminal tracking-[3px] text-yellow-600 uppercase">Card States</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: Lock, label: 'Hidden', desc: 'Classified data', color: 'text-purple-400', borderColor: 'border-purple-800/30' },
            { icon: Eye, label: 'Revealed', desc: 'Temporarily shown', color: 'text-yellow-400', borderColor: 'border-yellow-800/30' },
            { icon: Unlock, label: 'Matched', desc: 'Pair confirmed', color: 'text-green-400', borderColor: 'border-green-800/30' },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + i * 0.1 }}
              className={`text-center p-2 rounded border ${item.borderColor} bg-black/30`}>
              <item.icon size={16} className={`${item.color} mx-auto mb-1`} />
              <p className={`text-xs font-terminal tracking-wider ${item.color}`}>{item.label}</p>
              <p className="text-xs font-terminal text-gray-600 mt-0.5">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Rating info */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
        className="text-center">
        <div className="inline-flex items-center gap-2 bg-purple-950/20 border border-purple-800/20 rounded px-3 py-1.5">
          <Award size={10} className="text-yellow-500" />
          <span className="text-xs font-terminal text-purple-400/80 tracking-wider">Performance rated by move count — aim for S rank!</span>
        </div>
      </motion.div>

      {/* Start button */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
        className="text-center pt-2">
        <motion.button
          whileHover={{ scale: 1.03, boxShadow: '0 0 25px rgba(168,85,247,0.2)' }}
          whileTap={{ scale: 0.97 }}
          onClick={onStart}
          className="group relative px-10 py-3 bg-gradient-to-r from-purple-950/50 via-purple-900/30 to-purple-950/50 border-2 border-purple-600/60 text-purple-400 font-terminal text-base tracking-[5px] uppercase transition-all hover:border-purple-500/80 overflow-hidden"
        >
          <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/8 to-transparent pointer-events-none"
            animate={{ x: ['-200%', '200%'] }} transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 0.5 }} />
          <motion.div className="absolute inset-0 border border-purple-500/20 rounded-sm pointer-events-none"
            animate={{ opacity: [0, 0.5, 0] }} transition={{ duration: 2, repeat: Infinity }} />
          <span className="relative z-10 flex items-center justify-center gap-2">
            <Scan size={14} /> Begin Scan <ChevronRight size={14} />
          </span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

/* ─── Enhanced Security HUD ─── */
function SecurityHUD({ moves, matched, total, elapsedTime }) {
  const rating = getRating(moves);
  const mins = Math.floor(elapsedTime / 60);
  const secs = elapsedTime % 60;

  return (
    <div className="max-w-lg mx-auto mb-4">
      <div className="flex items-center justify-between bg-black/40 border border-purple-900/30 rounded px-4 py-2">
        <div className="flex items-center gap-3">
          <Scan size={14} className="text-purple-500" />
          <span className="text-xs font-terminal text-purple-600 tracking-wider">PATTERN SCAN ACTIVE</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Timer */}
          <div className="flex items-center gap-1">
            <Timer size={10} className="text-gray-600" />
            <span className="text-xs font-orbitron text-gray-500">{mins}:{String(secs).padStart(2, '0')}</span>
          </div>
          <div className="h-4 w-px bg-purple-900/30" />
          {/* Moves */}
          <div className="flex items-center gap-1">
            <span className="text-xs font-terminal text-gray-600">MOVES:</span>
            <span className="text-sm font-orbitron text-yellow-400">{moves}</span>
          </div>
          <div className="h-4 w-px bg-purple-900/30" />
          {/* Rating */}
          {moves > 0 && (
            <>
              <span className="text-sm font-orbitron font-bold" style={{ color: rating.color }}>{rating.letter}</span>
              <div className="h-4 w-px bg-purple-900/30" />
            </>
          )}
          {/* Matched */}
          <div className="flex items-center gap-1">
            <span className="text-xs font-terminal text-gray-600">PAIRS:</span>
            <span className="text-sm font-orbitron text-green-400">{matched}<span className="text-green-700">/{total}</span></span>
          </div>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-1 bg-black/60 mt-1 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-purple-700 to-green-500 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${(matched / total) * 100}%`, boxShadow: '0 0 8px rgba(168,85,247,0.4)' }}
        />
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export default function Puzzle3MemoryMatch({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [puzzleActive, setPuzzleActive] = useState(false);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [moves, setMoves] = useState(0);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [mismatchIds, setMismatchIds] = useState([]);
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const lockRef = useRef(false);
  const timerRef = useRef(null);

  const cards = useMemo(() => {
    const pairs = [];
    CARD_ICONS.forEach((icon, i) => {
      pairs.push({ id: i * 2, name: icon.name, Icon: icon.Icon, color: icon.color });
      pairs.push({ id: i * 2 + 1, name: icon.name, Icon: icon.Icon, color: icon.color });
    });
    return shuffle(pairs);
  }, []);

  // Mount-reset
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setPuzzleActive(false);
    setSolved(false);
    setShowFinalLeon(false);
    setMoves(0);
    setFlipped([]);
    setMatched([]);
    setMismatchIds([]);
    setSuccessPhase(0);
    setShowProceed(false);
    setElapsedTime(0);
    lockRef.current = false;
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Live timer
  useEffect(() => {
    if (puzzleActive && !solved) {
      timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [puzzleActive, solved]);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setPuzzleActive(true);
  }, []);

  const isFlipped = (id) => flipped.includes(id);
  const isMatched = (id) => matched.includes(id);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCardClick = useCallback((card) => {
    if (!puzzleActive || lockRef.current) return;
    if (isFlipped(card.id) || isMatched(card.id)) return;

    const newFlipped = [...flipped, card.id];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      lockRef.current = true;

      const first = cards.find(c => c.id === newFlipped[0]);
      const second = cards.find(c => c.id === newFlipped[1]);

      if (first.name === second.name) {
        setTimeout(() => {
          const newMatched = [...matched, first.id, second.id];
          setMatched(newMatched);
          setFlipped([]);
          lockRef.current = false;

          if (newMatched.length === cards.length) {
            setSolved(true);
            setPuzzleActive(false);
            // Multi-phase success
            setSuccessPhase(1);
            setTimeout(() => setSuccessPhase(2), 500);
            setTimeout(() => setSuccessPhase(3), 1200);
            setTimeout(() => setSuccessPhase(4), 1800);
            setTimeout(() => setShowFinalLeon(true), 2200);
          }
        }, 500);
      } else {
        // Mismatch flash
        setMismatchIds([newFlipped[0], newFlipped[1]]);
        setTimeout(() => {
          setFlipped([]);
          setMismatchIds([]);
          lockRef.current = false;
        }, 900);
      }
    }
  }, [puzzleActive, flipped, matched, cards]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.SECURITY_BADGE);
    setShowProceed(true);
  }, [onItemCollected]);

  const rating = getRating(moves);

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-memory-match">
      <SecurityBackground />

      {/* Flash overlays */}
      <AnimatePresence>
        {successPhase >= 1 && (
          <>
            <motion.div initial={{ opacity: 0.5 }} animate={{ opacity: 0 }} transition={{ duration: 1 }}
              className="absolute inset-0 bg-purple-500/10 pointer-events-none z-40" />
            <motion.div
              initial={{ width: 0, height: 0, opacity: 0.5 }}
              animate={{ width: 600, height: 600, opacity: 0 }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-purple-400/40 pointer-events-none z-40"
            />
          </>
        )}
      </AnimatePresence>

      {/* HUD Top Bar */}
      {(puzzleActive || solved) && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="relative z-10 flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
            <span className="text-xs font-terminal tracking-[3px] text-purple-500 uppercase">Checkpoint 05 // Security Clearance</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield size={10} className="text-purple-600" />
            <span className="text-xs font-terminal tracking-wider text-purple-600 uppercase">Clearance Level: 3</span>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <div className="text-center mb-4 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-px bg-gradient-to-r from-transparent to-purple-700/40" />
          <Grid3X3 size={12} className="text-purple-500" />
          <p className="text-xs font-terminal tracking-[6px] text-purple-500 uppercase">Checkpoint 05</p>
          <Grid3X3 size={12} className="text-purple-500" />
          <div className="w-8 h-px bg-gradient-to-l from-transparent to-purple-700/40" />
        </div>
        <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">Security Clearance</h2>
        <p className="text-sm text-purple-600 font-terminal mt-1">Pattern Recognition // Match the classified symbols</p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <MemoryTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {(puzzleActive || solved) && (
        <SecurityHUD moves={moves} matched={matched.length / 2} total={8} elapsedTime={elapsedTime} />
      )}

      {(puzzleActive || solved) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative z-10">
          {/* Card grid container */}
          <div className="max-w-lg mx-auto">
            <div className="hud-bracket bg-black/40 p-5 relative overflow-hidden"
              style={{ boxShadow: '0 0 15px rgba(168,85,247,0.05), inset 0 0 25px rgba(0,0,0,0.4)' }}>
              {/* Corner decorations */}
              <div className="absolute top-2 left-2 w-4 h-4 border-t border-l border-purple-800/30" />
              <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-purple-800/30" />
              <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-purple-800/30" />
              <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-purple-800/30" />

              {/* Grid */}
              <div className="grid gap-2 md:gap-3" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }} data-testid="memory-grid">
                {cards.map((card, idx) => {
                  const revealed = isFlipped(card.id) || isMatched(card.id);
                  const cardMatched = isMatched(card.id);
                  const isMismatch = mismatchIds.includes(card.id);
                  const CardIcon = card.Icon;

                  return (
                    <motion.div
                      key={card.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.03, duration: 0.2 }}
                      whileHover={!revealed ? { scale: 1.04 } : {}}
                      whileTap={!revealed ? { scale: 0.96 } : {}}
                      onClick={() => handleCardClick(card)}
                      className="aspect-square cursor-pointer"
                      data-testid={`memory-card-${card.id}`}
                    >
                      <div className={`flip-card w-full h-full ${revealed ? 'flipped' : ''}`}>
                        <div className="flip-card-inner">
                          {/* Front - Hidden */}
                          <div className={`flip-card-front rounded-lg border-2 transition-all duration-300 relative overflow-hidden
                            ${cardMatched
                              ? 'border-green-600/40 bg-green-950/20'
                              : 'border-purple-900/30 bg-gradient-to-br from-[#0d0d0d] to-[#0a0a0a] hover:border-purple-700/50 hover:shadow-[0_0_12px_rgba(168,85,247,0.15)]'
                            }`}
                          >
                            {!cardMatched && (
                              <motion.div
                                animate={{ y: ['0%', '100%'] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                className="absolute inset-x-0 h-4 pointer-events-none"
                                style={{ background: 'linear-gradient(to bottom, transparent, rgba(168,85,247,0.05), transparent)' }}
                              />
                            )}
                            <div className="flex flex-col items-center gap-1 opacity-40">
                              <UmbrellaLogo size={28} />
                              <span className="text-[6px] font-terminal text-purple-700 tracking-wider">CLASSIFIED</span>
                            </div>
                            <div className="absolute inset-0 opacity-5"
                              style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(168,85,247,0.5) 4px, rgba(168,85,247,0.5) 5px), repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(168,85,247,0.5) 4px, rgba(168,85,247,0.5) 5px)' }}
                            />
                          </div>

                          {/* Back - Revealed */}
                          <div className={`flip-card-back rounded-lg border-2 transition-all duration-300 relative overflow-hidden
                            ${cardMatched
                              ? 'border-green-500/60 bg-gradient-to-br from-green-950/40 to-green-950/20'
                              : isMismatch
                                ? 'border-red-500/60 bg-gradient-to-br from-red-950/20 to-[#0a0a0a]'
                                : 'border-yellow-600/50 bg-gradient-to-br from-[#111] to-[#0a0a0a]'
                            }`}
                          >
                            {cardMatched && (
                              <div className="absolute inset-0 bg-green-500/5 pointer-events-none animate-pulse" style={{ animationDuration: '3s' }} />
                            )}
                            <CardIcon
                              size={36}
                              strokeWidth={1.5}
                              className="transition-all"
                              style={{
                                color: cardMatched ? '#22c55e' : card.color,
                                filter: `drop-shadow(0 0 12px ${cardMatched ? 'rgba(34,197,94,0.6)' : card.color + '90'})`,
                              }}
                            />
                            {cardMatched && (
                              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-1 right-1">
                                <Unlock size={10} className="text-green-400" />
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Legend */}
          {puzzleActive && (
            <div className="flex justify-center gap-4 mt-4 flex-wrap">
              {[
                { icon: Lock, label: 'Hidden', color: 'text-purple-500' },
                { icon: Eye, label: 'Revealed', color: 'text-yellow-400' },
                { icon: Unlock, label: 'Matched', color: 'text-green-400' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded border border-gray-800/30">
                  <item.icon size={10} className={item.color} />
                  <span className={`text-xs font-terminal tracking-wider ${item.color}`}>{item.label.toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Multi-phase success */}
      {solved && (
        <div className="text-center space-y-4 mt-6 relative z-10">
          {/* Phase 2: Icon */}
          <AnimatePresence>
            {successPhase >= 2 && (
              <motion.div initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                className="relative w-20 h-20 mx-auto">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 border-2 border-dashed border-purple-500/20 rounded-full" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-purple-900/30 border-2 border-purple-500 flex items-center justify-center"
                    style={{ boxShadow: '0 0 30px rgba(168,85,247,0.25)' }}>
                    <Shield size={28} className="text-purple-400" />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 3: Title */}
          <AnimatePresence>
            {successPhase >= 3 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <motion.p initial={{ letterSpacing: '2px' }} animate={{ letterSpacing: '6px' }}
                  transition={{ duration: 0.8 }}
                  className="font-orbitron text-purple-400 text-sm">
                  CLEARANCE GRANTED
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 4: Stats */}
          <AnimatePresence>
            {successPhase >= 4 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="space-y-3">
                {/* Stats row */}
                <div className="inline-flex items-center gap-4 bg-purple-950/20 border border-purple-800/20 rounded px-4 py-2">
                  <div className="text-center">
                    <p className="text-xs font-terminal text-gray-600 tracking-wider">MOVES</p>
                    <p className="text-sm font-orbitron text-yellow-400">{moves}</p>
                  </div>
                  <div className="w-px h-6 bg-purple-800/30" />
                  <div className="text-center">
                    <p className="text-xs font-terminal text-gray-600 tracking-wider">TIME</p>
                    <p className="text-sm font-orbitron text-gray-400">{Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')}</p>
                  </div>
                  <div className="w-px h-6 bg-purple-800/30" />
                  <div className="text-center">
                    <p className="text-xs font-terminal text-gray-600 tracking-wider">RATING</p>
                    <motion.p initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}
                      className="text-lg font-orbitron font-bold" style={{ color: rating.color }}>{rating.letter}</motion.p>
                  </div>
                </div>
                <p className="text-xs font-terminal tracking-[3px] uppercase" style={{ color: rating.color }}>{rating.label}</p>
                <p className="text-gray-400 font-rajdhani">
                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                    className="text-yellow-500/80" style={{ textShadow: '0 0 8px rgba(234,179,8,0.3)' }}>
                    Security Badge acquired
                  </motion.span>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {showProceed && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="text-center mt-8 relative z-20"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onComplete?.()}
            className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
          >
            <span className="relative z-10">PROCEED</span>
            <motion.div
              className="absolute inset-0"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
            />
          </motion.button>
        </motion.div>
      )}

      {showLeon && (
        <LeonDialogue
          message={PUZZLE_3_MEMORY_MATCH.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={PUZZLE_3_MEMORY_MATCH.leon.complete(moves)}
          onDismiss={handleFinalDismiss}
        />
      )}
    </div>
  );
}
